import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IdeliveryNote,
  IfilterProps,
  TdeliveryNoteRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TdeliveryNoteProps = IdeliveryNote<number, IcommerceUser>;

/**
 * Represents a delivery note in the system.
 * This class provides methods to manage and track delivery notes,
 * which are documents that accompany a shipment of goods.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TdeliveryNoteProps - The type definition for delivery note properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link DeliveryNote} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const deliveryNote = await DeliveryNote.getAll();
 *
 * const deliveryNote = await DeliveryNote.filterAll({
 *
 * });
 *
 * const deliveryNote = await DeliveryNote.bulkDelete({
 *
 * });
 *
 * const deliveryNote = await DeliveryNote.getOne('resourceId');
 *
 * const deliveryNote = await DeliveryNote.remove();
 *
 * ```
 */
export class DeliveryNote extends DatabaseAuto {
  /** Base API endpoint for delivery note operations */
  static readonly baseUrl = '/delivery-note';

  /** Unique reference ID for the delivery note */
  readonly urId: string;

  /** Current status of the delivery note */
  readonly deliveryNoteStatus: TdeliveryNoteProps['deliveryNoteStatus'];

  /** Unique number identifying the delivery note */
  readonly deliveryNoteNumber: TdeliveryNoteProps['deliveryNoteNumber'];

  /** Related invoice information */
  readonly invoiceRelated: TdeliveryNoteProps['invoiceRelated'];

  /** Company associated with the delivery note */
  readonly company: TdeliveryNoteProps['company'];

  /**
   * Creates a new instance of DeliveryNote.
   * @param {TdeliveryNoteProps} data - The delivery note data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TdeliveryNoteProps) {
    super(data);

    this.urId = data.urId;
    this.deliveryNoteStatus = data.deliveryNoteStatus;
    this.deliveryNoteNumber = data.deliveryNoteNumber;
    this.invoiceRelated = data.invoiceRelated;
    this.company = data.company;
  }

  /**
   * Retrieves all delivery notes with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: DeliveryNote[] }>}
   * An object containing the total count and an array of delivery notes
   * @throws {Error} If there's an error fetching the delivery notes
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TdeliveryNoteProps>>(`${DeliveryNote.baseUrl}?${query}`);
    const deliveryNotes = await lastValueFrom(observer$);

    return {
      count: deliveryNotes.count,
      dataArr: deliveryNotes.data.map((val) => new DeliveryNote(val))
    };
  }

  /**
   * Filters delivery notes based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: DeliveryNote[] }>}
   * An object containing the total count and an array of filtered delivery notes
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TdeliveryNoteProps>>(
        `${DeliveryNote.baseUrl}/filter`,
        filter
      );
    const deliveryNotes = await lastValueFrom(observer$);

    return {
      count: deliveryNotes.count,
      dataArr: deliveryNotes.data.map((val) => new DeliveryNote(val))
    };
  }

  /**
   * Retrieves a single delivery note by its ID.
   * @param {string} resourceId - The unique identifier of the delivery note to retrieve
   * @returns {Promise<DeliveryNote>} The requested delivery note
   * @throws {Error} If the delivery note is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TdeliveryNoteProps>(`${DeliveryNote.baseUrl}/${resourceId}`);
    const deliveryNote = await lastValueFrom(observer$);

    return new DeliveryNote(deliveryNote);
  }

  /**
   * Creates a new delivery note record.
   * @param {TdeliveryNoteRequest<true>} payload - The delivery note data to create
   * @returns {Promise<DeliveryNote>} The newly created delivery note
   * @throws {Error} If the creation fails
   */
  static async add(payload: TdeliveryNoteRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TdeliveryNoteProps>(`${DeliveryNote.baseUrl}`, payload);

    const added = await lastValueFrom(observer$);

    return new DeliveryNote(added);
  }

  /**
   * Deletes multiple delivery notes based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which delivery notes to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${DeliveryNote.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current delivery note with new values.
   * @param {TdeliveryNoteRequest} payload - The data to update the delivery note with
   * @returns {Promise<DeliveryNote>} The updated delivery note
   * @throws {Error} If the update operation fails
   */
  async update(payload: TdeliveryNoteRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TdeliveryNoteProps>(
        `${DeliveryNote.baseUrl}/${this._id}`,
        payload
      );

    return new DeliveryNote(await lastValueFrom(observer$));
  }

  /**
   * Removes the current delivery note from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the delivery note doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${DeliveryNote.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
